import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  EVENT_TYPE_LABELS,
  FamilyMember,
  getDisplayName,
  getPerson,
  getTree,
} from "../api/trees";
import {
  ConnectedToPersonInfo,
  getConnectedPersonsForPerson,
  getRelationshipTypeLabel,
} from "../api/connectedPersons";
import Header from "../components/Common/Header";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import { EditPersonForm, MediaGallery } from "../components/Person";
import { AncestryTreeView } from "../components/Tree";
import {
  LocationSummary,
  getLocationsForPerson,
  tagItemToLocation,
  untagItemFromLocation,
} from "../api/locations";
import { LocationPicker } from "../components/Location";

type TabType = "timeline" | "tree";

export default function PersonView() {
  const { treeId, personId } = useParams<{
    treeId: string;
    personId: string;
  }>();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("timeline");
  const [showAddLocation, setShowAddLocation] = useState(false);

  // Build National Archives search URL from person data
  const buildArchiveSearchUrl = () => {
    if (!person) return "/national-archives";

    const params = new URLSearchParams();

    // Use full name or surname as the search query
    const searchName = person.surname || displayName;
    if (searchName) {
      params.set("q", searchName);
    }

    // Extract birth year for date filtering
    if (person.birth_date) {
      const birthYear = new Date(person.birth_date).getFullYear();
      if (!isNaN(birthYear)) {
        // Search from 10 years before birth to cover census records
        params.set("dateFrom", String(birthYear - 10));
        // If there's a death date, use that; otherwise search to birth + 100
        if (person.death_date) {
          const deathYear = new Date(person.death_date).getFullYear();
          if (!isNaN(deathYear)) {
            params.set("dateTo", String(deathYear + 10));
          }
        } else {
          params.set("dateTo", String(birthYear + 100));
        }
      }
    }

    return `/national-archives?${params.toString()}`;
  };

  const { data: tree } = useQuery({
    queryKey: ["tree", treeId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getTree(Number(treeId), token);
    },
    enabled: !!treeId,
  });

  const {
    data: person,
    isLoading,
    error,
    refetch: refetchPerson,
  } = useQuery({
    queryKey: ["person", treeId, personId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getPerson(Number(treeId), Number(personId), token);
    },
    enabled: !!treeId && !!personId,
  });

  // Fetch connected persons for this person
  const { data: connectedPersons } = useQuery<ConnectedToPersonInfo[]>({
    queryKey: ["connectedPersonsFor", personId],
    queryFn: async () => {
      if (!personId) return [];
      const token = await getAccessTokenSilently();
      return getConnectedPersonsForPerson(parseInt(personId), token);
    },
    enabled: !!personId,
  });

  // Fetch locations for this person
  const { data: personLocations } = useQuery<LocationSummary[]>({
    queryKey: ["personLocations", personId],
    queryFn: async () => {
      if (!personId) return [];
      const token = await getAccessTokenSilently();
      return getLocationsForPerson(token, parseInt(personId));
    },
    enabled: !!personId,
  });

  // Tag location mutation
  const tagLocationMutation = useMutation({
    mutationFn: async (locationId: number) => {
      if (!personId) throw new Error("No person ID");
      const token = await getAccessTokenSilently();
      return tagItemToLocation(token, locationId, { person_id: parseInt(personId) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personLocations", personId] });
      setShowAddLocation(false);
    },
  });

  // Untag location mutation
  const untagLocationMutation = useMutation({
    mutationFn: async (locationId: number) => {
      if (!personId) throw new Error("No person ID");
      const token = await getAccessTokenSilently();
      return untagItemFromLocation(token, locationId, { person_id: parseInt(personId) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personLocations", personId] });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <div className="flex items-center justify-center py-24">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            Failed to load person. Please try again.
          </div>
        </main>
      </div>
    );
  }

  const displayName = getDisplayName(person);
  
  // Log whenever component re-renders
  console.log('PersonView rendering with person:', person.given_name, person.surname, 'isEditing:', isEditing);

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Back button and Breadcrumb */}
        <div className="mb-4 flex items-center gap-4">
          <button
            onClick={() => navigate(`/trees/${treeId}`)}
            className="flex items-center gap-1 text-vault-600 hover:text-vault-900 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
            Back
          </button>
          <nav>
            <ol className="flex items-center gap-2 text-sm text-vault-600">
              <li>
                <Link to="/dashboard" className="hover:text-vault-900">
                  Dashboard
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link
                  to={`/trees/${treeId}`}
                  className="hover:text-vault-900"
                >
                  {tree?.name || "Tree"}
                </Link>
              </li>
              <li>/</li>
              <li className="text-vault-900">{displayName}</li>
            </ol>
          </nav>
        </div>

        {/* Person Header */}
        <div className="mb-8 rounded-lg bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div
                className={`flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full text-2xl font-bold text-white ${
                  person.sex === "M"
                    ? "bg-blue-500"
                    : person.sex === "F"
                      ? "bg-pink-500"
                      : "bg-vault-400"
                }`}
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="font-serif text-3xl font-bold text-vault-900">
                  {displayName}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-vault-600">
                  {person.birth_date_text && (
                    <span>
                      <span className="font-medium">Born:</span>{" "}
                      {person.birth_date_text}
                      {person.birth_place_text && ` in ${person.birth_place_text}`}
                    </span>
                  )}
                  {person.death_date_text && (
                    <span>
                      <span className="font-medium">Died:</span>{" "}
                      {person.death_date_text}
                      {person.death_place_text && ` in ${person.death_place_text}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(buildArchiveSearchUrl())}
                className="btn-secondary flex items-center gap-2"
                title="Search National Archives for records about this person"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-4 w-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                  />
                </svg>
                Search Archives
              </button>
              {tree?.user_can_edit && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                    />
                  </svg>
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Edit Form Modal */}
        {isEditing && (
          <EditPersonForm
            treeId={Number(treeId)}
            person={person}
            onClose={() => setIsEditing(false)}
            onSuccess={async () => {
              console.log('PersonView: onSuccess called, refetching...');
              console.log('PersonView: Current person data BEFORE refetch:', person);
              // Force refetch immediately
              const result = await refetchPerson();
              console.log('PersonView: refetch result:', result.data);
              console.log('PersonView: Current person data AFTER refetch:', person);
              console.log('PersonView: refetch complete, closing form');
              setIsEditing(false);
            }}
          />
        )}

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-vault-200">
          <nav className="-mb-px flex gap-4">
            <button
              onClick={() => setActiveTab("timeline")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === "timeline"
                  ? "border-vault-600 text-vault-900"
                  : "border-transparent text-vault-500 hover:border-vault-300 hover:text-vault-700"
              }`}
            >
              Timeline & Details
            </button>
            <button
              onClick={() => setActiveTab("tree")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === "tree"
                  ? "border-vault-600 text-vault-900"
                  : "border-transparent text-vault-500 hover:border-vault-300 hover:text-vault-700"
              }`}
            >
              Family Tree
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "tree" ? (
          <AncestryTreeView treeId={Number(treeId)} rootPersonId={person.id} />
        ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Timeline */}
          <div className="lg:col-span-2">
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="font-serif text-xl font-semibold text-vault-900">
                Timeline
              </h2>

              {person.events.length === 0 ? (
                <p className="mt-4 text-vault-600">No events recorded.</p>
              ) : (
                <div className="mt-4 space-y-4">
                  {person.events
                    .sort((a, b) => {
                      if (!a.date_start && !b.date_start) return 0;
                      if (!a.date_start) return 1;
                      if (!b.date_start) return -1;
                      return a.date_start.localeCompare(b.date_start);
                    })
                    .map((event) => (
                      <div
                        key={event.id}
                        className="flex gap-4 border-l-2 border-vault-200 pl-4"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-vault-100 px-2 py-0.5 text-xs font-medium text-vault-700">
                              {EVENT_TYPE_LABELS[event.event_type] ||
                                event.event_type}
                            </span>
                            {event.date_text && (
                              <span className="text-sm text-vault-500">
                                {event.date_text}
                              </span>
                            )}
                          </div>
                          {event.place_text && (
                            <p className="mt-1 text-sm text-vault-600">
                              📍 {event.place_text}
                            </p>
                          )}
                          {event.description && (
                            <p className="mt-1 text-sm text-vault-600">
                              {event.description}
                            </p>
                          )}
                          {event.notes && (
                            <p className="mt-1 text-sm italic text-vault-500">
                              {event.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {person.notes && (
                <div className="mt-6 border-t border-vault-100 pt-4">
                  <h3 className="font-medium text-vault-900">Notes</h3>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-vault-600">
                    {person.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Media Gallery */}
            <MediaGallery treeId={Number(treeId)} personId={person.id} />
          </div>

          {/* Family */}
          <div className="space-y-6">
            {/* Parents */}
            {person.parents.length > 0 && (
              <FamilySection
                title="Parents"
                members={person.parents}
                treeId={Number(treeId)}
              />
            )}

            {/* Siblings */}
            {person.siblings.length > 0 && (
              <FamilySection
                title="Siblings"
                members={person.siblings}
                treeId={Number(treeId)}
              />
            )}

            {/* Spouses */}
            {person.spouses.length > 0 && (
              <FamilySection
                title="Spouses"
                members={person.spouses}
                treeId={Number(treeId)}
              />
            )}

            {/* Children */}
            {person.children.length > 0 && (
              <FamilySection
                title="Children"
                members={person.children}
                treeId={Number(treeId)}
              />
            )}

            {/* No family */}
            {person.parents.length === 0 &&
              person.siblings.length === 0 &&
              person.spouses.length === 0 &&
              person.children.length === 0 && (
                <div className="rounded-lg bg-white p-6 shadow-sm">
                  <h2 className="font-serif text-xl font-semibold text-vault-900">
                    Family
                  </h2>
                  <p className="mt-4 text-vault-600">
                    No family connections recorded.
                  </p>
                </div>
              )}

            {/* Connected People (Non-Family) */}
            {connectedPersons && connectedPersons.length > 0 && (
              <ConnectedPeopleSection connections={connectedPersons} />
            )}

            {/* Locations */}
            <div className="rounded-lg bg-white p-6 shadow-sm mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-lg font-semibold text-vault-900">
                  Locations ({personLocations?.length || 0})
                </h2>
                <button
                  onClick={() => setShowAddLocation(!showAddLocation)}
                  className="text-sm text-vault-600 hover:text-vault-900 flex items-center gap-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Location
                </button>
              </div>
              {showAddLocation && (
                <div className="mb-4 p-4 bg-vault-50 rounded-lg">
                  <LocationPicker
                    value={null}
                    onChange={(id) => { if (id) { tagLocationMutation.mutate(id); } }}
                    placeholder="Search for a location to add..."
                  />
                  <button
                    onClick={() => setShowAddLocation(false)}
                    className="mt-2 text-sm text-vault-600 hover:text-vault-900"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {!personLocations || personLocations.length === 0 ? (
                <p className="text-vault-500 text-center py-2">No locations tagged yet.</p>
              ) : (
                <div className="space-y-2">
                  {personLocations.map((loc) => (
                    <div key={loc.id} className="flex items-center justify-between py-2 border-b border-vault-100 last:border-0">
                      <Link
                        to={`/locations/${loc.id}`}
                        className="flex items-center gap-2 text-vault-900 hover:text-vault-600"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-medium">{loc.name}</span>
                        {(loc.city || loc.country) && (
                          <span className="text-sm text-vault-500">
                            ({[loc.city, loc.country].filter(Boolean).join(", ")})
                          </span>
                        )}
                      </Link>
                      <button
                        onClick={() => untagLocationMutation.mutate(loc.id)}
                        className="text-vault-400 hover:text-red-600 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </main>
    </div>
  );
}

function FamilySection({
  title,
  members,
  treeId,
}: {
  title: string;
  members: FamilyMember[];
  treeId: number;
}) {
  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="font-serif text-lg font-semibold text-vault-900">
        {title}
      </h2>
      <ul className="mt-3 space-y-2">
        {members.map((member) => (
          <li key={member.id}>
            <Link
              to={`/trees/${treeId}/persons/${member.id}`}
              className="flex items-center gap-2 rounded-lg p-2 transition-colors hover:bg-vault-50"
            >
              <div
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm text-white ${
                  member.sex === "M"
                    ? "bg-blue-500"
                    : member.sex === "F"
                      ? "bg-pink-500"
                      : "bg-vault-400"
                }`}
              >
                {getDisplayName(member).charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-vault-900">
                  {getDisplayName(member)}
                </p>
                {(member.birth_date_text || member.death_date_text) && (
                  <p className="truncate text-xs text-vault-500">
                    {member.birth_date_text && `b. ${member.birth_date_text}`}
                    {member.birth_date_text &&
                      member.death_date_text &&
                      " – "}
                    {member.death_date_text && `d. ${member.death_date_text}`}
                  </p>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConnectedPeopleSection({
  connections,
}: {
  connections: ConnectedToPersonInfo[];
}) {
  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2">
        <h2 className="font-serif text-lg font-semibold text-vault-900">
          Connected People
        </h2>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
          Non-Family
        </span>
      </div>
      <ul className="mt-3 space-y-2">
        {connections.map((conn) => (
          <li key={conn.id}>
            <Link
              to={`/connected-persons/${conn.connected_person_id}`}
              className="flex items-center gap-2 rounded-lg p-2 transition-colors hover:bg-amber-50"
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 text-sm">
                ⚭
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-vault-900">
                  {conn.connected_person_name}
                </p>
                <p className="truncate text-xs text-vault-500">
                  {getRelationshipTypeLabel(conn.relationship_type)}
                  {conn.relationship_description && ` · ${conn.relationship_description}`}
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      <Link
        to="/connected-persons"
        className="mt-3 block text-sm text-vault-600 hover:text-vault-900"
      >
        View all connected people →
      </Link>
    </div>
  );
}